import React, { useEffect, useMemo, useState } from "react";
import { Backdrop, CircularProgress } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { Wrapper } from "components/wrapper";
import {
  DepartmentCard,
  DepartmentDetails,
  DepartmentHeader,
  DepartmentList,
  DepartmentName,
  DetailItem,
  DetailLabel,
  DetailValue,
  MoreInfoLink,
  ResponsibleCard,
  ResponsibleGrid,
  ResponsibleName,
  ResponsiblePhoto,
  ResponsiblePhotoPlaceholder,
  ResponsibleRole,
  TierBlock,
  TierLabel,
  ToggleIcon,
} from "./organizationalStructure.styles";
import { useOrganizationalStructureState } from "./context/organizational-structure-context";
import { useOrganizationalStructureController } from "./context/organizational-structure-controller";
import { IGovernamentalStructureReponse } from "models/response/governamental-strutucture-response";
import { PhoneFormatter } from "utils/phone-formatter";
import ArrowDown from "assets/icons/arrow-down.svg";

const RESPONSAVEL_TYPE = 1;

interface DepartmentRowProps {
  department: IGovernamentalStructureReponse;
}

const DepartmentRow: React.FC<DepartmentRowProps> = ({ department }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <DepartmentCard>
      <DepartmentHeader
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <DepartmentName>{department.person}</DepartmentName>
        <ToggleIcon src={ArrowDown} alt="" aria-hidden="true" $expanded={expanded} />
      </DepartmentHeader>

      {expanded && (
        <DepartmentDetails>
          {department.openingHours && (
            <DetailItem>
              <DetailLabel>Horário de atendimento</DetailLabel>
              <DetailValue>{department.openingHours}</DetailValue>
            </DetailItem>
          )}

          {department.address && (
            <DetailItem>
              <DetailLabel>Endereço</DetailLabel>
              <DetailValue>{department.address}</DetailValue>
            </DetailItem>
          )}

          {department.phoneContact && (
            <DetailItem>
              <DetailLabel>Telefone</DetailLabel>
              <DetailValue>{PhoneFormatter.format(department.phoneContact)}</DetailValue>
            </DetailItem>
          )}

          {department.email && (
            <DetailItem>
              <DetailLabel>E-mail</DetailLabel>
              <DetailValue>{department.email}</DetailValue>
            </DetailItem>
          )}

          <MoreInfoLink href={`/estrutura-organizacional/${department.id}`}>
            Ver mais informações
          </MoreInfoLink>
        </DepartmentDetails>
      )}
    </DepartmentCard>
  );
};

export const OrganizationalStructurePage = () => {
  const { governamentalStructures, loading } = useOrganizationalStructureState();
  const { onInit } = useOrganizationalStructureController();

  useEffect(() => {
    onInit();
  }, [onInit]);

  // Divide os registros em dois níveis: liderança (Prefeito/Vice) em destaque
  // e secretarias/departamentos em lista abaixo. O cadastro usa o campo "type"
  // (Responsável/Departamento) para essa distinção, mas na prática muitos
  // cadastros usam "Responsável" tanto para o(a) prefeito(a) quanto para
  // secretários(as) — nesse caso, usamos o nome do cargo como sinal de
  // liderança (Prefeito/Vice-Prefeito), evitando depender só do campo "type".
  const PREFEITO_REGEX = /prefeito/i;

  const { responsaveis, departamentos } = useMemo(() => {
    const byType = {
      responsaveis: governamentalStructures.filter((s) => s.type === RESPONSAVEL_TYPE),
      departamentos: governamentalStructures.filter((s) => s.type !== RESPONSAVEL_TYPE),
    };

    // O campo "type" realmente separa os dois grupos: usa como está.
    if (byType.departamentos.length > 0) {
      return byType;
    }

    // Sem distinção real por "type": infere pela presença de "Prefeito"
    // no nome/cargo cadastrado (cobre "Prefeito" e "Vice-Prefeito").
    return {
      responsaveis: governamentalStructures.filter((s) => PREFEITO_REGEX.test(s.person)),
      departamentos: governamentalStructures.filter((s) => !PREFEITO_REGEX.test(s.person)),
    };
  }, [governamentalStructures]);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Estrutura Organizacional" },
        ]}
      />
      <PageTitle>ESTRUTURA ORGANIZACIONAL</PageTitle>
      <Wrapper>

        {responsaveis.length > 0 && (
          <TierBlock>
            <TierLabel>Gestores e Responsáveis</TierLabel>
            <ResponsibleGrid>
              {responsaveis.map((responsavel) => (
                <ResponsibleCard
                  key={responsavel.id}
                  href={`/estrutura-organizacional/${responsavel.id}`}
                >
                  {responsavel.imageUrl ? (
                    <ResponsiblePhoto src={responsavel.imageUrl} alt={responsavel.person} />
                  ) : (
                    <ResponsiblePhotoPlaceholder>
                      {getInitials(responsavel.person)}
                    </ResponsiblePhotoPlaceholder>
                  )}
                  <ResponsibleName>{responsavel.person}</ResponsibleName>
                  {responsavel.desType && (
                    <ResponsibleRole>{responsavel.desType}</ResponsibleRole>
                  )}
                </ResponsibleCard>
              ))}
            </ResponsibleGrid>
          </TierBlock>
        )}

        {departamentos.length > 0 && (
          <TierBlock>
            <TierLabel>Secretarias e Departamentos</TierLabel>
            <DepartmentList>
              {departamentos.map((departamento) => (
                <DepartmentRow key={departamento.id} department={departamento} />
              ))}
            </DepartmentList>
          </TierBlock>
        )}

      </Wrapper>
      <Backdrop open={loading} style={{ zIndex: 1000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};
